import { config } from './config';

/**
 * The signed-in operator, as the app needs them (D20, 18.12).
 *
 * `oid` is the Entra object id and is the identity the API stores in
 * `glass.jobs.operator_oid` — the audit trail. It is NOT `sub`: `sub` is pairwise per
 * application, so two apps see different values for one person, and a job attributed by
 * `sub` could not be matched to the same operator anywhere else.
 */
export interface Account {
  oid: string;
  upn: string;
  name: string;
}

/**
 * What the app asks of an identity provider, with two implementations behind a platform
 * extension: MSAL on web, AppAuth on the device.
 *
 * Deliberately the same shape as `apps/review`'s `AuthBackend`. Two consoles signing into
 * one tenant with one app registration should not have two different ideas of what signing
 * in means, and the review console's version has already been through a phase of use.
 */
export interface AuthBackend {
  /** Finish any pending redirect and report the signed-in account, if there is one. */
  init(): Promise<Account | null>;
  /**
   * Start sign-in. Takes NO arguments: the provider owns the credential prompt now, and
   * the app never sees an email or a password.
   */
  signIn(): Promise<Account | null>;
  signOut(): Promise<void>;
  /** The ACCESS token for the API scope, never the ID token. `force` skips the cache. */
  getAccessToken(force?: boolean): Promise<string | null>;
}

/**
 * The authority to talk to.
 *
 * Normally derived from the tenant, exactly as the API derives its issuer. `ENTRA_ISSUER`
 * overrides it, and exists for ONE reason: the end-to-end run signs in against a local
 * mock provider, and without an override neither half of the round trip can be pointed at
 * anything but `login.microsoftonline.com`. A production bundle carries the empty string
 * — webpack's `EnvironmentPlugin` substitutes a value for every declared key, so emptiness
 * is what "unset" means here and the branch below is dead code in a release.
 *
 * Note the asymmetry, which mirrors real Entra: this is the AUTHORITY, and MSAL appends
 * `/v2.0` to it for discovery. The token's `iss` therefore ends in `/v2.0`, so the API's
 * own `ENTRA_ISSUER` is this value plus that suffix — `EntraIssuer()` does the same thing
 * for a real tenant.
 */
export function authority(): string {
  return config.entraIssuer || `https://login.microsoftonline.com/${config.entraTenantId}`;
}

/** True when the authority is the real Entra, which decides MSAL's protocol mode. */
export function isRealEntra(): boolean {
  return !config.entraIssuer;
}

/**
 * The one scope the app requests.
 *
 * One scope, never Graph. A Graph token carries a different `aud` and the API's verifier
 * rejects it — correctly — with a 401 that says the audience is wrong, which reads like a
 * misconfigured API rather than a request for the wrong token.
 */
export function apiScopes(): string[] {
  return [config.entraApiScope];
}

/**
 * A sign-in failure, split the way the screen draws it: a bold lead in the failure colour
 * and the rest in ink. One string could not do that without the screen re-parsing it.
 *
 * `offline` is carried separately because it is the one failure the operator can act on
 * immediately, and frame 00 answers it differently — the button says "Try again" rather
 * than "Sign in", because nothing about what they did was wrong.
 */
export interface AuthError {
  title: string;
  detail: string;
  offline: boolean;
}

/** The provider's own words are never shown: they name protocols the operator cannot act on. */
export function describeAuthError(e: unknown): AuthError {
  const raw = e instanceof Error ? `${e.name}: ${e.message}` : String(e);

  /*
    MSAL names its transport failures rather than describing them, so none of the words
    above appear. `endpoints_resolution_error` is the one a phone in a basement produces:
    MSAL fetches the authority's metadata before it can send anyone anywhere, so a
    signal-less sign-in fails there first and never reaches a network-shaped message.

    It is not ONLY no signal — a blocked `connect-src` produces the identical code, which
    is how a CSP that had stopped naming the issuer read as an offline phone for a whole
    CI run. That is the right trade anyway: the operator can act on "check your signal"
    and can act on nothing else, and a misconfigured CSP names itself in the console in
    the one place someone can fix it.
  */
  if (
    /network|fetch|offline|ENOTFOUND|Failed to fetch/i.test(raw) ||
    /endpoints_resolution_error|openid_config_error|no_network_connectivity|post_request_failed|get_request_failed/i.test(
      raw,
    )
  ) {
    return {
      title: "Can't connect.",
      detail: 'Check your signal and try again.',
      offline: true,
    };
  }
  /*
    The operator cancelled — closed the browser tab, or pressed back out of Custom Tabs.
    Not a failure, and saying "sign-in failed" for it trains people to ignore the message.
  */
  if (/user_cancelled|cancell?ed|access_denied|popup_window_error/i.test(raw)) {
    return {
      title: 'Sign-in cancelled.',
      detail: 'Nothing was sent. Tap Sign in when you are ready.',
      offline: false,
    };
  }
  return {
    title: "Couldn't sign you in.",
    detail: 'Try again. If it keeps happening, contact support.',
    offline: false,
  };
}
